import { useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  tableHead: { backgroundColor: "#FAFBFC" },
  tableHeadCell: {
    color: TOKENS.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${TOKENS.border}`,
    padding: "14px 20px",
  },
  tableRow: {
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: "#FAFBFC" },
  },
  tableCell: {
    padding: "16px 20px",
    borderBottom: `1px solid ${TOKENS.divider}`,
    fontSize: 14,
    color: TOKENS.textPrimary,
    verticalAlign: "middle",
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  nameText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    lineHeight: 1.3,
  },
  emailText: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 1,
  },
  bookingBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 28,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: TOKENS.divider,
    color: TOKENS.textPrimary,
  },
  emptyState: { padding: "64px 20px", textAlign: "center" },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: 4,
  },
  emptySub: { fontSize: 13, color: TOKENS.textSecondary },
  pagination: {
    borderTop: `1px solid ${TOKENS.border}`,
    "& .MuiTablePagination-toolbar": { paddingLeft: 16, paddingRight: 8 },
    "& .MuiTablePagination-caption": {
      fontSize: 13,
      color: TOKENS.textSecondary,
    },
  },
}));

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const CustomerListResults = ({ customers = [], ...rest }) => {
  const classes = useStyles();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handlePageChange = (_event, newPage) => setPage(newPage);

  const paged = customers.slice(page * limit, page * limit + limit);

  return (
    <Card className={classes.card} {...rest}>
      {customers.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIcon}>—</Box>
          <Typography className={classes.emptyTitle}>
            No customers yet
          </Typography>
          <Typography className={classes.emptySub}>
            When you add customers, they'll show up here.
          </Typography>
        </Box>
      ) : (
        <>
          <PerfectScrollbar>
            <Box style={{ minWidth: 700 }}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>
                      Customer
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Phone
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Bookings
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((customer) => (
                    <TableRow
                      hover
                      key={customer.id}
                      className={classes.tableRow}
                    >
                      <TableCell className={classes.tableCell}>
                        <Box className={classes.nameCell}>
                          <Box className={classes.avatar}>
                            {getInitials(customer.full_name)}
                          </Box>
                          <Box>
                            <Typography className={classes.nameText}>
                              {customer.full_name || "—"}
                            </Typography>
                            <Typography className={classes.emailText}>
                              {customer.email || "—"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {customer.phone_number || "—"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <span className={classes.bookingBadge}>
                          {customer.booking_count || 0}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>
          <TablePagination
            component="div"
            className={classes.pagination}
            count={customers.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}
    </Card>
  );
};

export default CustomerListResults;
